import { HealthConfidenceState, TaxonomyFacet } from '@edgebox/sync-core-data-definitions';

/**
 * Pure, framework-free join logic for gap assignment details.
 *
 * The after-name confidence badge, its tooltip, and the "Has" line reasoning are
 * supplied by Sync Core on each `health[]` entry (`confidence`, `confidenceState`,
 * `explanation`) and only rendered here — the Embed performs no confidence math and
 * no formula classification. This module joins `ContentItemEntity.terms` against the
 * structured `formula.parsed` term references to drive the per-section (Should have /
 * Has / Quality) badges, which keep using the individual term confidences.
 */

/** The three feature facets, longest-prefix-first so prefix stripping is unambiguous. */
const FEATURE_FACETS: readonly string[] = [
  TaxonomyFacet.ContentFeatureSuggestion, // 'content-feature-suggestion' — Should have
  TaxonomyFacet.ContentFeatureQuality, //    'content-feature-quality'    — Quality of
  TaxonomyFacet.ContentFeature, //           'content-feature'            — Has
];

export type GapFeatureFacetKind = 'shouldHave' | 'has' | 'quality';

const FACET_TO_KIND: Record<string, GapFeatureFacetKind> = {
  [TaxonomyFacet.ContentFeatureSuggestion]: 'shouldHave',
  [TaxonomyFacet.ContentFeature]: 'has',
  [TaxonomyFacet.ContentFeatureQuality]: 'quality',
};

/** Projection of `formula.parsed` that Embed retains for gap assignment details. */
export interface HealthCheckFormulaReference {
  inputTermKeys: string[];
  outputTermKeys: string[];
  functionsUsed: string[];
}

/** Structural input shapes — loose so both runtime and serialized term assignments fit. */
export interface GapTermAssignmentInput {
  facet?: unknown;
  term?: unknown;
  source?: { type?: unknown } | null;
  confidence?: number | null;
  reasoning?: string | null;
}

export interface GapTermsInput {
  assigned?: readonly GapTermAssignmentInput[] | null;
  evaluated?: readonly GapTermAssignmentInput[] | null;
  disallowed?: readonly GapTermAssignmentInput[] | null;
}

export interface GapTermAssignmentView {
  facetKind: GapFeatureFacetKind;
  termKey: string;
  /** 0..1, or null when the producer recorded no confidence. */
  confidence: number | null;
  reasoning: string | null;
  disallowed: boolean;
}

export interface GapFeatureAssignments {
  identity: string;
  shouldHave?: GapTermAssignmentView;
  has?: GapTermAssignmentView;
  quality?: GapTermAssignmentView;
  /** User-removed terms, tracked separately so they can be flagged in the UI. */
  disallowed: Partial<Record<GapFeatureFacetKind, GapTermAssignmentView>>;
}

/** Resolve a stable key from a runtime/serialized reference (or a bare string). */
export function extractReferenceKey(reference: unknown): string | undefined {
  if (!reference) {
    return undefined;
  }

  if (typeof reference === 'string') {
    const key = reference.trim();
    return key.length ? key : undefined;
  }

  if (typeof reference !== 'object') {
    return undefined;
  }

  const candidate = reference as {
    getStableKey?: () => string | undefined;
    stableKey?: string;
    key?: string;
  };

  const methodKey = candidate.getStableKey?.();
  if (typeof methodKey === 'string' && methodKey.trim().length > 0) {
    return methodKey;
  }

  if (typeof candidate.stableKey === 'string' && candidate.stableKey.trim().length > 0) {
    return candidate.stableKey;
  }

  if (typeof candidate.key === 'string' && candidate.key.trim().length > 0) {
    return candidate.key;
  }

  return undefined;
}

/**
 * Strip the feature-facet prefix from a term key to get the shared feature identity.
 * E.g. `content-feature-suggestion.structure.answer-block` → `structure.answer-block`.
 * Returns `undefined` for non-feature terms.
 */
export function toFeatureIdentity(termKey: string | undefined): string | undefined {
  if (!termKey) {
    return undefined;
  }

  for (const facet of FEATURE_FACETS) {
    const prefix = `${facet}.`;
    if (termKey.startsWith(prefix)) {
      const identity = termKey.slice(prefix.length);
      return identity.length ? identity : undefined;
    }
  }

  return undefined;
}

function normalizeConfidence(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }

  return Math.max(0, Math.min(1, value));
}

function normalizeReasoning(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

/** Convert a 0..1 confidence to a 0..100 percentage (or null). */
export function toConfidencePercentage(confidence: number | null): number | null {
  if (confidence === null) {
    return null;
  }

  return Math.round(Math.max(0, Math.min(1, confidence)) * 100);
}

function toAssignmentView(
  assignment: GapTermAssignmentInput,
  disallowed: boolean
): { identity: string; view: GapTermAssignmentView } | undefined {
  const facetKind = typeof assignment.facet === 'string' ? FACET_TO_KIND[assignment.facet] : undefined;
  if (!facetKind) {
    return undefined;
  }

  const termKey = extractReferenceKey(assignment.term);
  const identity = toFeatureIdentity(termKey);
  if (!identity || !termKey) {
    return undefined;
  }

  return {
    identity,
    view: {
      facetKind,
      termKey,
      confidence: normalizeConfidence(assignment.confidence),
      reasoning: normalizeReasoning(assignment.reasoning),
      disallowed,
    },
  };
}

/**
 * Build a per-feature lookup over `assigned ∪ evaluated` (a term may live in either),
 * keeping `disallowed` separate so user-removed features can be flagged. Indexed by the
 * shared feature identity. Assigned wins over evaluated for the same facet.
 */
export function buildFeatureAssignmentLookup(terms: GapTermsInput | undefined): Map<string, GapFeatureAssignments> {
  const lookup = new Map<string, GapFeatureAssignments>();
  if (!terms) {
    return lookup;
  }

  const ensure = (identity: string): GapFeatureAssignments => {
    let existing = lookup.get(identity);
    if (!existing) {
      existing = { identity, disallowed: {} };
      lookup.set(identity, existing);
    }
    return existing;
  };

  const ingest = (assignments: readonly GapTermAssignmentInput[] | null | undefined, asDisallowed: boolean) => {
    for (const assignment of assignments || []) {
      const resolved = toAssignmentView(assignment, asDisallowed);
      if (!resolved) {
        continue;
      }

      const entry = ensure(resolved.identity);
      const { facetKind } = resolved.view;

      if (asDisallowed) {
        if (!entry.disallowed[facetKind]) {
          entry.disallowed[facetKind] = resolved.view;
        }
        continue;
      }

      if (facetKind === 'shouldHave' && !entry.shouldHave) {
        entry.shouldHave = resolved.view;
      } else if (facetKind === 'has' && !entry.has) {
        entry.has = resolved.view;
      } else if (facetKind === 'quality' && !entry.quality) {
        entry.quality = resolved.view;
      }
    }
  };

  ingest(terms.assigned, false);
  ingest(terms.evaluated, false);
  ingest(terms.disallowed, true);

  return lookup;
}

/** One rendered line of the detail panel. A gap always has exactly two: "Should have" and "Has". */
export type GapDetailLineKind = 'shouldHave' | 'has';

export interface GapDetailLine {
  kind: GapDetailLineKind;
  label: string;
  /**
   * `present` — a value is shown (an AI term's reasoning, or the formula requirement/issue text).
   * `missing` — the page does not have the feature (Has line only) → renders "Not detected".
   * `removed` — the user explicitly removed this feature term → renders "Removed by user".
   */
  status: 'present' | 'missing' | 'removed';
  /** Percentage 0..100, or `null` for no badge. Never derived from a quality term. */
  confidence: number | null;
  text: string | null;
}

export interface GapAssignmentDetail {
  /** Exactly two lines (Should have, Has) for the inline detail panel. */
  sections: GapDetailLine[];
}

/**
 * Tooltip copy for the after-name confidence badge, keyed off the backend-supplied
 * `health[].confidenceState`. The Embed never derives this — it only renders the
 * state Sync Core computed (`exact` = deterministic, `estimated` = AI, `unknown`/absent
 * = not available).
 */
export function describeConfidenceState(state: HealthConfidenceState | null | undefined): string {
  switch (state) {
    case HealthConfidenceState.Exact:
      return 'Confidence this rating is correct — computed deterministically.';
    case HealthConfidenceState.Estimated:
      return 'Confidence this rating is correct — an AI estimate.';
    default:
      return 'Confidence this rating is correct — not available.';
  }
}

/** Strip the leading "Detects pages …" detector phrasing so the description reads as a page issue. */
const FORMULA_ISSUE_PREFIX = /^detects\s+pages?\s+(?:(?:with|where|that\s+have|that\s+are|containing|having)\s+)?/i;

export function describeFormulaIssue(description: string | null): string | null {
  const normalized = normalizeReasoning(description);
  if (!normalized) {
    return null;
  }

  const stripped = normalized.replace(FORMULA_ISSUE_PREFIX, '').trim();
  const text = stripped.length ? stripped : normalized;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * A single `IssueEntity.evidence[]` item as it reaches the Embed (loose-typed: the rest client
 * hands back runtime instances, but tests pass plain objects). Produced by Sync Core for
 * formula-based checks — the actionable "what/why" the formula computed (a count, a score, the
 * list of offending links) that `HEALTH_SCORE(...)` would otherwise discard.
 */
export interface GapIssueEvidence {
  path?: string | null;
  value?: unknown;
  reasoning?: string | null;
  isTruncated?: boolean;
}

/** Render an evidence `value` inline. Primitives and arrays of primitives only; objects are skipped. */
function formatEvidenceValue(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : null;
  }

  if (typeof value === 'boolean') {
    return value ? 'yes' : 'no';
  }

  if (Array.isArray(value)) {
    const parts = value.map((entry) => formatEvidenceValue(entry)).filter((entry): entry is string => !!entry);
    return parts.length ? parts.join(', ') : null;
  }

  return null;
}

const REGEX_SPECIAL = /[.*+?^${}()|[\]\\]/g;

/**
 * Whether `reasoning` already spells out `value` as a distinct token, so appending it would only
 * duplicate. Uses word boundaries for alphanumeric values so a value like "3" isn't considered
 * present inside "13 items"; falls back to plain containment for values that can't sit on a `\b`
 * boundary (e.g. a comma-joined list).
 */
function reasoningContainsValue(reasoning: string, value: string): boolean {
  const escaped = value.replace(REGEX_SPECIAL, '\\$&');
  if (/^[\w].*[\w]$|^[\w]$/.test(value)) {
    return new RegExp(`\\b${escaped}\\b`).test(reasoning);
  }

  return reasoning.includes(value);
}

/** One evidence line: the reasoning, with the value appended only when it adds information. */
function describeEvidenceItem(item: GapIssueEvidence): string | null {
  const reasoning = normalizeReasoning(item.reasoning);
  const value = formatEvidenceValue(item.value);

  if (reasoning && value) {
    // The value is "useful" only when it isn't already spelled out in the reasoning text.
    return reasoningContainsValue(reasoning, value) ? reasoning : `${reasoning} (${value})`;
  }

  return reasoning ?? value ?? null;
}

/**
 * Turn an issue's formula evidence into a single human-readable "Has" line. Returns `null` when
 * no item carries usable reasoning/value, so callers fall back to the health-check description.
 */
export function describeFormulaEvidence(evidence: readonly GapIssueEvidence[] | undefined | null): string | null {
  if (!evidence?.length) {
    return null;
  }

  const lines = evidence.map((item) => describeEvidenceItem(item)).filter((line): line is string => !!line);
  if (!lines.length) {
    return null;
  }

  return lines.join('; ');
}

/**
 * "Has" text for a healthy check, where the page already satisfies the requirement. A green check
 * has no gap, so the Has line must never regurgitate the failure description or read "Not detected".
 */
const HEALTHY_HAS_TEXT = 'Meets this requirement.';

/** Build the "Should have" line from a feature's suggestion assignment (assigned or user-removed). */
function buildShouldHaveLine(assigned: GapTermAssignmentView | undefined, disallowed: GapTermAssignmentView | undefined): GapDetailLine {
  if (assigned) {
    return {
      kind: 'shouldHave',
      label: 'Should have',
      status: 'present',
      confidence: toConfidencePercentage(assigned.confidence),
      text: assigned.reasoning,
    };
  }

  if (disallowed) {
    return { kind: 'shouldHave', label: 'Should have', status: 'removed', confidence: null, text: null };
  }

  // The Should have line simply carries no detail (it must never read "Should have: Not detected").
  return { kind: 'shouldHave', label: 'Should have', status: 'present', confidence: null, text: null };
}

/**
 * Build the "Has" line from a feature's has assignment. The reasoning text prefers the
 * backend-supplied `explanation` (the per-page reasoning for the rating, which exists for passing
 * checks too) and falls back to the term's own reasoning on historical rows that predate it.
 */
function buildHasLine(
  assigned: GapTermAssignmentView | undefined,
  disallowed: GapTermAssignmentView | undefined,
  isHealthy: boolean,
  explanation: string | null
): GapDetailLine {
  if (disallowed) {
    return { kind: 'has', label: 'Has', status: 'removed', confidence: null, text: null };
  }

  if (assigned) {
    return {
      kind: 'has',
      label: 'Has',
      status: 'present',
      confidence: toConfidencePercentage(assigned.confidence),
      text: explanation ?? assigned.reasoning,
    };
  }

  // No has assignment. A healthy check has no gap, so its Has line affirms the requirement is met
  // (using the backend explanation when present) instead of reading "Not detected".
  if (isHealthy) {
    return { kind: 'has', label: 'Has', status: 'present', confidence: null, text: explanation ?? HEALTHY_HAS_TEXT };
  }

  return { kind: 'has', label: 'Has', status: 'missing', confidence: null, text: explanation };
}

/**
 * Resolve the gap's two-line (Should have / Has) detail-panel view-model by joining the health
 * check's `formula.parsed` term references against the per-feature assignment lookup.
 *
 * Whether the feature lines render is decided purely by data presence — a check with
 * `content-feature*` term assignments renders the Should have / Has lines (each with its own term
 * confidence badge), while a check without them falls back to the check name + reasoning. There is
 * no AI-vs-formula classification here; the after-name badge confidence and its tooltip live on the
 * `health[]` entry (`confidence` / `confidenceState`) and are rendered directly by the table.
 *
 * The "Has" line reasoning is the backend-supplied `explanation` (per-page, present for passing
 * checks too), falling back to formula evidence / term reasoning / the reworded description on rows
 * that predate it. A healthy (100%) check never restates the failure description or reads
 * "Not detected".
 */
export function resolveGapAssignmentDetail(args: {
  formula?: HealthCheckFormulaReference;
  healthCheckName?: string | null;
  healthCheckDescription?: string | null;
  /** Formula evidence loaded from the gap's issue, a fallback for the "Has" line of formula checks. */
  evidence?: readonly GapIssueEvidence[] | null;
  /** The check's score as a 0..100 percentage (or null/undefined when scoreless). 100 means healthy. */
  scorePercentage?: number | null;
  /** Backend reasoning for the rating (`health[].explanation`); the preferred "Has" line text. */
  explanation?: string | null;
  lookup: Map<string, GapFeatureAssignments>;
}): GapAssignmentDetail {
  const { formula, healthCheckName, healthCheckDescription, evidence, scorePercentage, explanation, lookup } = args;
  const name = normalizeReasoning(healthCheckName);
  const description = normalizeReasoning(healthCheckDescription);
  const explanationText = normalizeReasoning(explanation);
  // Healthy = a perfect score. Only then is the gap "closed", so the Has line goes positive.
  const isHealthy = scorePercentage === 100;

  const identities = new Set<string>();
  for (const termKey of [...(formula?.inputTermKeys || []), ...(formula?.outputTermKeys || [])]) {
    const identity = toFeatureIdentity(termKey);
    if (identity) {
      identities.add(identity);
    }
  }

  // Resolve the first feature identity that has any assignment data.
  let feature: GapFeatureAssignments | undefined;
  for (const identity of identities) {
    const candidate = lookup.get(identity);
    if (candidate) {
      feature = candidate;
      break;
    }
  }

  const shouldHave = feature?.shouldHave;
  const has = feature?.has;
  const disallowedShouldHave = feature?.disallowed.shouldHave;
  const disallowedHas = feature?.disallowed.has;

  // The feature lines render when there is should-have / has data (assigned or user-removed). A
  // quality-only feature has neither, so it renders the formula shape. Quality is never read here.
  const hasFeatureLines = !!(shouldHave || has || disallowedShouldHave || disallowedHas);

  if (hasFeatureLines) {
    return {
      sections: [buildShouldHaveLine(shouldHave, disallowedShouldHave), buildHasLine(has, disallowedHas, isHealthy, explanationText)],
    };
  }

  // Formula check (no feature term data): same two-line shape, derived from the check metadata.
  //   Should have — the requirement headline (the check name, e.g. "Exactly one h1").
  //   Has         — the page state. The backend explanation when present, then the formula's own
  //                 evidence (a count, a score, the list of offending links). Otherwise: a healthy
  //                 check affirms the requirement is met, while a gap falls back to the description
  //                 reworded away from the raw "Detects pages …" phrasing.
  const detected = explanationText ?? describeFormulaEvidence(evidence);
  const issueText = detected ?? (isHealthy ? HEALTHY_HAS_TEXT : describeFormulaIssue(description));

  return {
    sections: [
      { kind: 'shouldHave', label: 'Should have', status: 'present', confidence: null, text: name },
      { kind: 'has', label: 'Has', status: 'present', confidence: null, text: issueText },
    ],
  };
}
